import {useState} from "react";
import {Box, Collapse, Drawer, Stack, Typography, useTheme} from "@mui/material";
import {Link, useLocation} from "react-router-dom";
import {
  FiLock,
  FiLogOut,
  FiUsers,
  FiMonitor,
  FiSettings,
  FiKey,
  FiShield,
  FiBriefcase,
  FiPackage,
  FiClipboard,
  FiEye,
  FiChevronDown,
  FiChevronUp,
  FiHome
} from "react-icons/fi";
import logo from "../../assets/ts-logo.png";
import logo_blanc from "../../assets/ts-logo-blanco.png";
import useLogout from "../../hooks/useLogout";
import styles from "./Sidebar.module.css";
import MenuItem from "../MenuItem/MenuItem";
import {useAuthPermissions} from "../../hooks/useAuthPermissions";
import {useThemeStore} from "../../stores/useThemeStore";
import ThemeToggleSimple from "../ui/ThemeButton";

const Sidebar = () => {
  const {logout} = useLogout();
  const {verifyScope} = useAuthPermissions();
  const {isDark} = useThemeStore();
  const location = useLocation();

  const theme = useTheme();

  const handleLogout = () => {
    logout();
  };

  const isActivePath = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const sectionLabels = ["AUTH SERVICE", "COMPONENTES", "SEGURIDAD"];
  const [openSections, setOpenSections] = useState(Object.fromEntries(sectionLabels.map((l) => [l, true])));

  const toggleSection = (label) => setOpenSections((prev) => ({...prev, [label]: !prev[label]}));

  const sections = [
    {
      items: [{text: "Dashboard", icon: <FiHome />, path: "/dashboard", scope: "identity.users.global.read"}]
    },
    {
      label: "AUTH SERVICE",
      items: [
        {text: "Usuarios", icon: <FiUsers />, path: "/users", scope: "identity.users.global.read"},
        {text: "Tenants", icon: <FiBriefcase />, path: "/tenants", scope: "identity.tenants.global.read"}
      ]
    },
    {
      label: "COMPONENTES",
      items: [
        {text: "Productos", icon: <FiPackage />, path: "/products", scope: "identity.products.global.read"},
        {text: "Roles", icon: <FiLock />, path: "/roles", scope: "identity.roles.global.read"},
        {text: "Scopes", icon: <FiSettings />, path: "/scopes", scope: "identity.scopes.global.read"}
      ]
    },
    {
      label: "SEGURIDAD",
      items: [
        {text: "Clientes", icon: <FiMonitor />, path: "/clients", scope: "identity.clients.global.read"},
        {text: "Tokens", icon: <FiKey />, path: "/tokens", scope: "identity.tokens.global.read"},
        {text: "M2M Tokens", icon: <FiShield />, path: "/m2m-tokens", scope: "identity.m2m_tokens.global.read"},
        {
          text: "Tickets",
          icon: <FiClipboard />,
          path: "/tickets",
          scope: ["identity.tickets.global.read", "identity.tickets.global.write"]
        },
        {text: "Usuarios Panel", icon: <FiUsers />, path: "/usuarios-panel", scope: "identity.users.tenant.read"},
        {text: "Prev. Scopes", icon: <FiEye />, path: "/scopes-preview", scope: "identity.scopes.global.read"}
      ]
    }
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: "224px",
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: "224px",
          backgroundColor: theme.palette.sidebar.background,
          borderRight: `1px solid ${theme.palette.sidebar.border}`,
          boxSizing: "border-box",
          overflow: "hidden"
        }
      }}
    >
      <Box className={styles.container}>
        <Link className={styles.header} to={"/"}>
          <img src={isDark() ? logo_blanc : logo} alt="Logo" className={styles.logo} />
        </Link>

        <Box className={styles.scrollArea}>
          {sections.map(({label, items}) => {
            const visibleItems = items.filter(({scope}) => !scope || verifyScope(scope));
            if (visibleItems.length === 0) return null;
            const isCollapsible = !!label;
            const isOpen = !isCollapsible || openSections[label];
            return (
              <Box key={label} className={styles.section}>
                {isCollapsible && (
                  <Box
                    className={styles.title}
                    onClick={() => toggleSection(label)}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      userSelect: "none"
                    }}
                  >
                    <Typography variant="sidebarTitle" fontWeight="bold" color={theme.palette.sidebar.title}>
                      {label}
                    </Typography>
                    {isOpen ? (
                      <FiChevronUp size={13} color={theme.palette.sidebar.title} />
                    ) : (
                      <FiChevronDown size={13} color={theme.palette.sidebar.title} />
                    )}
                  </Box>
                )}
                <Collapse in={isOpen} timeout="auto">
                  <Stack direction="column" spacing={1} className={styles.menu}>
                    {visibleItems.map(({text, icon, path, onClick}) => (
                      <MenuItem
                        key={text}
                        text={text}
                        icon={icon}
                        onClick={onClick}
                        path={path}
                        active={isActivePath(path)}
                      />
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            );
          })}
        </Box>

        <Box className={styles.footer}>
          {/* BOTÓN LIGHT / DARK */}
          <Box mb={1.5}>
            <ThemeToggleSimple />
          </Box>
          <MenuItem key={"Salir"} text={"Salir"} icon={<FiLogOut color="#d53c65" />} onClick={handleLogout}></MenuItem>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
